import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Save } from "lucide-react";

/**
 * Admin-only modal to edit any user's full profile including:
 * - contact (email, phone), roll number, admission details, guardian info
 * - batch assignments (multi-select)
 * - parent-of link (for parent role)
 * - subscription plan/expiry
 */
export default function EditUserModal({ user, onClose, onSaved }) {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    batch_ids: user.batch_ids || [],
    parent_of: user.parent_of || "",
    roll_number: user.roll_number || "",
    address: user.address || "",
    date_of_birth: user.date_of_birth || "",
    admission_date: user.admission_date || "",
    admission_notes: user.admission_notes || "",
    guardian_name: user.guardian_name || "",
    guardian_phone: user.guardian_phone || "",
    emergency_contact: user.emergency_contact || "",
    subscription_plan: user.subscription_plan || "",
    subscription_expires_at: user.subscription_expires_at || "",
  }));

  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([
          api.get("/admin/batches"),
          api.get("/admin/users", { params: { role: "student" } }),
        ]);
        setBatches(b.data);
        setStudents(s.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Build payload: only send changed / non-empty relevant fields.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === undefined || v === null) return;
        payload[k] = v;
      });
      // Include explicit clears — allow clearing parent_of
      if (form.parent_of === "" && user.role === "parent") payload.parent_of = null;
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleBatch = (id, checked) => {
    setForm((f) => ({
      ...f,
      batch_ids: checked ? [...f.batch_ids, id] : f.batch_ids.filter((x) => x !== id),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="edit-user-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-heading font-black text-xl text-slate-900">Edit user</h2>
            <p className="text-xs text-slate-500 mt-0.5">{user.role} · {user.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="edit-user-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="eu-name" required />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="eu-email" required />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="eu-phone" />
            {user.role === "student" && (
              <>
                <Field label="Roll number" value={form.roll_number} onChange={(v) => setForm({ ...form, roll_number: v })} testId="eu-roll" />
                <Field label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testId="eu-dob" />
                <Field label="Admission date" type="date" value={form.admission_date} onChange={(v) => setForm({ ...form, admission_date: v })} testId="eu-admdate" />
                <Field label="Guardian name" value={form.guardian_name} onChange={(v) => setForm({ ...form, guardian_name: v })} testId="eu-guardian" />
                <Field label="Guardian phone" value={form.guardian_phone} onChange={(v) => setForm({ ...form, guardian_phone: v })} testId="eu-guardian-phone" />
                <Field label="Emergency contact" value={form.emergency_contact} onChange={(v) => setForm({ ...form, emergency_contact: v })} testId="eu-emergency" />
              </>
            )}
          </div>

          {user.role === "student" && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Address</label>
              <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="eu-address"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
          )}

          {user.role === "student" && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Admission notes</label>
              <textarea rows={2} value={form.admission_notes} onChange={(e) => setForm({ ...form, admission_notes: e.target.value })}
                data-testid="eu-adm-notes"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
          )}

          {(user.role === "student" || user.role === "teacher") && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Batches</label>
              <p className="text-xs text-slate-500 mb-1.5">
                {user.role === "student" ? "Assign / transfer between batches" : "Assign batches this teacher will run"}
              </p>
              <div className="max-h-40 overflow-auto p-2 rounded-lg border border-slate-200 space-y-1" data-testid="eu-batches">
                {batches.length === 0 && <p className="text-xs text-slate-500">No batches yet.</p>}
                {batches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm py-1">
                    <input type="checkbox"
                      checked={form.batch_ids.includes(b.id)}
                      onChange={(e) => toggleBatch(b.id, e.target.checked)}
                      data-testid={`eu-batch-${b.id}`} />
                    <span className="font-medium">{b.name}</span>
                    <span className="text-slate-400">· {b.subject}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {user.role === "parent" && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Linked student</label>
              <select value={form.parent_of} onChange={(e) => setForm({ ...form, parent_of: e.target.value })}
                data-testid="eu-parent-of"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none">
                <option value="">— none —</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
              </select>
            </div>
          )}

          <div className="pt-2 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="eu-save"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <input
        type={type} required={required} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
      />
    </div>
  );
}
